// @ts-check

/**
 * @file Renders the timelog detail route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {Link, useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Text, View} from "react-native"
import Button from "../../../src/components/button"
import buildRoutePath from "../../../src/shared/build-route-path"
import Card from "../../../src/components/card"
import DetailValue from "../../../src/components/detail-value"
import errorMessage from "../../../src/shared/error-message"
import {Timelog} from "../../../src/frontend-models/timelog"
import {requiredRouteParam} from "../../../src/shared/route-params"
import {useScreenLoading} from "../../../src/components/board-layout/screen-loading"
import ScreenTitle from "../../../src/components/screen-title"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {BoardThemeColors} from "../../../src/shared/theme" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * Formats seconds as hours and minutes.
 * @param {number | null | undefined} seconds Seconds to format.
 * @returns {string} Formatted time.
 */
function secondsToTime(seconds) {
  if (!seconds) return "0:00"
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - hours * 3600) / 60)
  return `${hours}:${String(minutes).padStart(2, "0")}`
}

/**
 * Formats a date for display.
 * @param {Date | string | null | undefined} value Date value to format.
 * @returns {string} Formatted date.
 */
function formatDate(value) {
  if (!value) return "-"
  const dateString =
    value instanceof Date
      ? value.toISOString().slice(0, 10)
      : String(value).slice(0, 10)
  const parsed = new Date(dateString + "T00:00:00")
  return Number.isNaN(parsed.getTime())
    ? dateString
    : parsed.toLocaleDateString("en")
}

/**
 * Checks whether a frontend-model lookup failed because the routed timelog is gone.
 * @param {string} message Error message to inspect.
 * @returns {boolean} True when the timelog is missing.
 */
function isTimelogNotFoundError(message) {
  return message.startsWith("Timelog not found")
}

/**
 * Logs stale timelog load errors while system-test debug logging is enabled.
 * @param {string} timelogID Timelog id from the stale request.
 * @param {string} message Error message that was ignored.
 * @returns {void} Nothing.
 */
function logIgnoredTimelogLoadError(timelogID, message) {
  if (typeof window === "undefined" || !window.location) return
  if (!window.location.search.includes("systemTestDebug=true")) return
  console.log("[awesome_tasks timelog-show] ignored stale load error", {
    href: window.location.href,
    message,
    timelogID
  })
}

/** @typedef {{[key: string]: unknown}} TimelogsShowScreenProps */

/**
 * @typedef {object} TimelogsShowScreenState
 * @property {boolean} loading Whether the timelog is loading.
 * @property {string | null} projectName Loaded project name.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {string | null} taskName Loaded task name.
 * @property {Timelog | null} timelog Loaded timelog record.
 * @property {string | null} userName Loaded user display name.
 */

/**
 * Timelog show screen using frontend models.
 * @augments {ShapeComponent<TimelogsShowScreenProps, TimelogsShowScreenState>}
 */
class TimelogsShowScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {number} */
  loadRequestId = 0

  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {string} */
  timelogID

  /** @type {TimelogsShowScreenState} */
  state = {
    loading: true,
    projectName: /** @type {string | null} */ null,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    taskName: /** @type {string | null} */ null,
    timelog: /** @type {Timelog | null} */ null,
    userName: /** @type {string | null} */ null
  }

  /** Initializes route params and timelog loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()

    const timelogID = requiredRouteParam(params, "id", "Timelog ID")
    this.colors = useThemeColors()
    this.params = params
    this.themeName = useThemeName()
    this.timelogID = timelogID
    useEffect(() => {
      const requestId = ++this.loadRequestId
      if (timelogID) {
        void this.loadTimelog(timelogID, requestId)
      } else {
        this.s.loading = false
        this.s.status = {
          kind: "error",
          text: _("Timelog not found")
        }
      }
      return () => {
        if (this.loadRequestId === requestId) this.loadRequestId += 1
      }
    }, [timelogID])
  }

  /**
   * Renders timelog details.
   * @returns {ReactNode} Timelog detail content.
   */
  render() {
    const {loading, projectName, status, taskName, timelog, userName} = this.s
    const {params, timelogID} = this.tt
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="timelogShowScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="timelogShowScreen"
      >
        <ScreenTitle>{_("Timelog")}</ScreenTitle>
        <View
          testID="timelogShowScreen/content"
        >
          {status &&
            <Text
              style={styles[`statusError-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="timelogShowScreen/status"
            >
              {status.text}
            </Text>
          }
          {timelog &&
            <Card
              style={styles.cardSpacing ||= {
                padding: 20
              }}
              testID="timelogShowScreen/card"
            >
              <View
                style={styles.headerRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 20
                }}
                testID="timelogShowScreen/headerRow"
              >
                <Text
                  style={styles[`headerTitle-${themeName}`] ||= {
                    color: colors.text,
                    fontSize: 24,
                    fontWeight: 700
                  }}
                  testID="timelogShowScreen/title"
                >
                  {_("Timelog")}
                </Text>
                <Button
                  href={buildRoutePath(params, `/timelogs/${timelogID}/edit`)}
                  iconName="pen-to-square"
                  label={_("Edit")}
                  testID="timelogShowScreen/editButton"
                />
              </View>
              {detailRow({
                colors,
                label: _("Date"),
                testID: "date",
                themeName,
                value: formatDate(timelog.date())
              })}
              {detailRow({
                colors,
                label: _("Time"),
                testID: "time",
                themeName,
                value: secondsToTime(timelog.timeSeconds())
              })}
              {detailRow({
                colors,
                label: _("Time transport"),
                testID: "timeTransport",
                themeName,
                value: secondsToTime(timelog.timeTransportSeconds())
              })}
              {detailRow({
                colors,
                label: _("Transport length (km)"),
                testID: "transportLength",
                themeName,
                value:
                  timelog.transportLength() == null
                    ? "-"
                    : `${timelog.transportLength()}`
              })}
              {detailRow({
                colors,
                href: timelog.task()?.project()?.id() != null
                  ? buildRoutePath(params, `/projects/${timelog.task()?.project()?.id()}`)
                  : null,
                label: _("Project"),
                testID: "project",
                themeName,
                value: projectName || "-"
              })}
              {detailRow({
                colors,
                href: timelog.task()?.id() != null
                  ? buildRoutePath(params, `/tasks/${timelog.task()?.id()}?projectId=${encodeURIComponent(timelog.task()?.projectId() || "")}`)
                  : null,
                label: _("Task"),
                testID: "task",
                themeName,
                value: taskName || "-"
              })}
              {detailRow({
                colors,
                href: timelog.user()?.id() != null
                  ? buildRoutePath(params, `/users/${timelog.user()?.id()}`)
                  : null,
                label: _("User"),
                testID: "user",
                themeName,
                value: userName || "-"
              })}
              {detailRow({
                colors,
                label: _("Invoiced"),
                testID: "invoiced",
                themeName,
                value: timelog.invoiced() ? _("Yes") : _("No")
              })}
              {detailRow({
                colors,
                label: _("Comment"),
                testID: "comment",
                themeName,
                value: timelog.comment() || "-"
              })}
            </Card>
          }
        </View>
      </View>
    )
  }

  /**
   * Loads the timelog referenced by the current route.
   * @param {string} timelogID Routed timelog id.
   * @param {number} requestId Request guard for this load.
   * @returns {Promise<void>} Resolves after screen state is updated.
   * @rejects {Error} When an unexpected load failure happens.
   */
  loadTimelog = async (timelogID, requestId) => {
    this.s.loading = true
    this.s.projectName = null
    this.s.status = null
    this.s.taskName = null
    this.s.timelog = null
    this.s.userName = null

    try {
      const timelog = await Timelog.preload({
        task: {
          project: true
        },
        user: true
      }).find(timelogID)
      if (this.loadRequestId !== requestId) return
      const task = timelog.task()
      const project = task?.project()
      const user = timelog.user()
      this.s.loading = false
      this.s.projectName = project?.name()
      this.s.taskName = task?.name()
      this.s.timelog = timelog
      this.s.userName = user
        ? [user.firstName(), user.lastName()]
            .filter(Boolean)
            .join(" ")
            .trim() || user.email()
        : null
    } catch (error) {
      const message = errorMessage(error)
      if (this.loadRequestId !== requestId) {
        logIgnoredTimelogLoadError(timelogID, message)
        return
      }
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: isTimelogNotFoundError(message) ? _("Timelog not found") : message
      }
      if (isTimelogNotFoundError(message)) return
      throw error
    }
  }

}

/** @type {NamedExoticComponent<TimelogsShowScreenProps>} */
const TimelogsShowScreenComponent = memo(shapeComponent(TimelogsShowScreen))

export default TimelogsShowScreenComponent

/**
 * @typedef {object} TimelogDetailRowArgs
 * @property {BoardThemeColors} colors Active theme palette.
 * @property {string | null} [href] Optional detail link target.
 * @property {string} label Detail label.
 * @property {string} testID Test identifier suffix.
 * @property {string} themeName Active theme name.
 * @property {string} value Detail value.
 */

/**
 * Renders one timelog detail row.
 * @param {TimelogDetailRowArgs} args Detail row values.
 * @returns {ReactNode} Rendered metadata row.
 */
function detailRow({colors, href, label, testID, themeName, value}) {
  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 10
      }}
      testID={`timelogShowScreen/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`timelogShowScreen/${testID}/label`}
      >
        {label}
      </Text>
      {href && value !== "-" ? (
        <Link
          accessibilityLabel={value}
          href={href}
          style={styles[`detailLinkValue-${themeName}`] ||= {
            color: colors.link,
            fontSize: 16,
            textDecorationLine: "underline"
          }}
          testID={`timelogShowScreen/${testID}/link`}
        >
          {value}
        </Link>
      ) : (
        <DetailValue
          style={styles[`detailValue-${themeName}`] ||= {
            color: colors.text,
            fontSize: 16
          }}
          testID={`timelogShowScreen/${testID}/value`}
          value={value}
        />
      )}
    </View>
  )
}
